import {
  applyStyles,
  createElement,
  ensureTrailingSlash,
  getActiveScript,
  injectStyles,
  mergeConfig
} from './utils.js';

const STYLE_ID = 'aifinex-panel-style';
const DEFAULT_CONFIG = {
  position: 'bottom-right',
  framePath: 'frame.html',
  assetBase: null,
  frameTitle: 'AIFINEX Support',
  brandName: '',
  brandLogo: '',
  welcomeMessage: '',
  navColor: '',
  bodyColor: '',
  brandNameColor: '',
  sendButtonColor: '#d3d4d5',
  sendIconColor: '#d3d4d5',
  userMessageColor: '',
  zIndex: 2147482000,
  panelWidth: 420,
  panelHeight: 560,
  offsetX: 24,
  offsetY: 24,
  borderRadius: 20,
  showCloseButton: false,
  themeColor: '#d3d4d5',
  mountSelector: null,
  inline: true,
  n8nUrl: '',
  sessionId: '',
  bubbleLauncher: false,
  launcherIcon: '💬',
  launcherImage: '',
  launcherAriaLabel: 'Sohbeti aç',
  bubblePosition: 'bottom-left',
  bubbleOffsetX: 24,
  bubbleOffsetY: 24,
  bubbleSize: 64,
  bubblePanelGap: 12,
  bubbleBackground: 'linear-gradient(135deg, #5b4bff, #7a6dff)',
  bubbleBorderColor: '',
  bubbleBounce: false,
  leadWebhookUrl: '',
  leadFormEnabled: true
};

function parseBubbleConfigAttribute(value) {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn('[aifinex-panel] data-bubble-config JSON parse edilemedi:', error);
    return null;
  }
}

function applyBubbleOverrides(target, bubbleConfig) {
  if (!bubbleConfig || typeof bubbleConfig !== 'object') {
    return;
  }
  const map = {
    enabled: 'bubbleLauncher',
    launcher: 'bubbleLauncher',
    icon: 'launcherIcon',
    launcherIcon: 'launcherIcon',
    image: 'launcherImage',
    ariaLabel: 'launcherAriaLabel',
    label: 'launcherAriaLabel',
    position: 'bubblePosition',
    offsetX: 'bubbleOffsetX',
    offsetY: 'bubbleOffsetY',
      size: 'bubbleSize',
    gap: 'bubblePanelGap',
    background: 'bubbleBackground',
      borderColor: 'bubbleBorderColor',
      bounce: 'bubbleBounce'
  };
  Object.entries(map).forEach(([sourceKey, targetKey]) => {
    if (bubbleConfig[sourceKey] !== undefined) {
      target[targetKey] = bubbleConfig[sourceKey];
    }
  });
  if (bubbleConfig.offset !== undefined) {
    if (typeof bubbleConfig.offset === 'object') {
      if (bubbleConfig.offset.x !== undefined) {
        target.bubbleOffsetX = bubbleConfig.offset.x;
      }
      if (bubbleConfig.offset.y !== undefined) {
        target.bubbleOffsetY = bubbleConfig.offset.y;
      }
    } else {
      target.bubbleOffsetX = bubbleConfig.offset;
      target.bubbleOffsetY = bubbleConfig.offset;
    }
  }
}

function toNumber(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizePosition(value) {
  return value === 'bottom-left' ? 'bottom-left' : 'bottom-right';
}

class AifinexPanel {
  constructor(customConfig = {}) {
    this.script = getActiveScript();
    this.config = this.resolveConfig(customConfig);
    this.assetBase = this.resolveAssetBase();
    this.frameUrl = this.resolveFrameUrl();
    this.root = null;
    this.iframe = null;
    this.launcher = null;
    this.useBubbleLauncher = false;
    this.documentListenerAttached = false;
    this.handleMessage = this.handleMessage.bind(this);
    this.handleLauncherClick = this.handleLauncherClick.bind(this);
    this.handleDocumentPointer = this.handleDocumentPointer.bind(this);
    this.handleLauncherAnimationEnd = this.handleLauncherAnimationEnd.bind(this);
    this.mountWhenReady();
  }

  resolveConfig(customConfig) {
    const dataset = this.script ? {
      framePath: this.script.dataset.framePath,
      assetBase: this.script.dataset.assetBase,
      position: this.script.dataset.position,
      panelWidth: this.script.dataset.panelWidth,
      panelHeight: this.script.dataset.panelHeight,
      offset: this.script.dataset.offset,
      offsetX: this.script.dataset.offsetX,
      offsetY: this.script.dataset.offsetY,
      borderRadius: this.script.dataset.borderRadius,
      showCloseButton: this.script.dataset.showCloseButton,
      themeColor: this.script.dataset.themeColor,
      mountSelector: this.script.dataset.mountSelector,
      inline: this.script.dataset.inline,
      brandName: this.script.dataset.brandName,
      brandLogo: this.script.dataset.brandLogo,
      welcomeMessage: this.script.dataset.welcomeMessage,
      navColor: this.script.dataset.navColor,
      bodyColor: this.script.dataset.bodyColor,
      brandNameColor: this.script.dataset.brandNameColor,
      sendButtonColor: this.script.dataset.sendButtonColor,
      sendIconColor: this.script.dataset.sendIconColor,
      userMessageColor: this.script.dataset.userMessageColor,
      n8nUrl: this.script.dataset.n8nUrl,
      sessionId: this.script.dataset.sessionId,
      bubbleLauncher: this.script.dataset.bubbleLauncher,
      leadWebhookUrl: this.script.dataset.leadWebhookUrl,
      leadFormEnabled: this.script.dataset.leadFormEnabled,
      launcherIcon: this.script.dataset.launcherIcon,
      launcherImage: this.script.dataset.launcherImage || this.script.dataset.bubbleIcon,
      launcherAriaLabel: this.script.dataset.launcherAriaLabel,
      bubblePosition: this.script.dataset.bubblePosition,
      bubbleOffsetX: this.script.dataset.bubbleOffsetX,
      bubbleOffsetY: this.script.dataset.bubbleOffsetY,
      bubbleSize: this.script.dataset.bubbleSize,
      bubblePanelGap: this.script.dataset.bubblePanelGap,
      bubbleBackground: this.script.dataset.bubbleBackground,
      bubbleBorderColor: this.script.dataset.bubbleBorderColor,
      bubbleBounce: this.script.dataset.bubbleBounce
    } : {};
    const bubbleAttributeConfig = parseBubbleConfigAttribute(this.script?.dataset?.bubbleConfig);
    if (bubbleAttributeConfig) {
      applyBubbleOverrides(dataset, bubbleAttributeConfig);
    }
    const normalizedCustom = customConfig && typeof customConfig === 'object'
      ? { ...customConfig }
      : {};
    if (normalizedCustom.bubble && typeof normalizedCustom.bubble === 'object') {
      applyBubbleOverrides(normalizedCustom, normalizedCustom.bubble);
      delete normalizedCustom.bubble;
    }
    const config = mergeConfig(DEFAULT_CONFIG, dataset, normalizedCustom);
    config.panelWidth = toNumber(config.panelWidth, DEFAULT_CONFIG.panelWidth);
    config.panelHeight = toNumber(config.panelHeight, DEFAULT_CONFIG.panelHeight);
    if (config.offset && !config.offsetX && !config.offsetY) {
      const offsetValue = toNumber(config.offset, 24);
      config.offsetX = offsetValue;
      config.offsetY = offsetValue;
    } else {
      config.offsetX = toNumber(config.offsetX, DEFAULT_CONFIG.offsetX);
      config.offsetY = toNumber(config.offsetY, DEFAULT_CONFIG.offsetY);
    }
    config.borderRadius = toNumber(config.borderRadius, DEFAULT_CONFIG.borderRadius);
    config.showCloseButton = `${config.showCloseButton}` === 'true';
    config.mountSelector = config.mountSelector || null;
    config.inline = config.mountSelector ? true : `${config.inline}` === 'true';
    config.sendButtonColor = (config.sendButtonColor && config.sendButtonColor.trim()) || DEFAULT_CONFIG.sendButtonColor;
    config.sendIconColor = (config.sendIconColor && config.sendIconColor.trim()) || DEFAULT_CONFIG.sendIconColor;
    config.userMessageColor = config.userMessageColor || '';
    config.bubbleLauncher = `${config.bubbleLauncher}` === 'true';
    config.launcherIcon = (config.launcherIcon && `${config.launcherIcon}`.trim()) || DEFAULT_CONFIG.launcherIcon;
    config.launcherAriaLabel = config.launcherAriaLabel || DEFAULT_CONFIG.launcherAriaLabel;
    config.bubblePosition = normalizePosition(config.bubblePosition || 'bottom-left');
    config.bubbleOffsetX = toNumber(
      config.bubbleOffsetX,
      Number.isFinite(config.offsetX) ? config.offsetX : DEFAULT_CONFIG.offsetX
    );
    config.bubbleOffsetY = toNumber(
      config.bubbleOffsetY,
      Number.isFinite(config.offsetY) ? config.offsetY : DEFAULT_CONFIG.offsetY
    );
    config.bubbleSize = Math.max(32, toNumber(config.bubbleSize, DEFAULT_CONFIG.bubbleSize));
    config.bubblePanelGap = Math.max(0, toNumber(config.bubblePanelGap, DEFAULT_CONFIG.bubblePanelGap));
    config.bubbleBackground = (config.bubbleBackground && config.bubbleBackground.trim()) || DEFAULT_CONFIG.bubbleBackground;
    config.bubbleBorderColor = (config.bubbleBorderColor && config.bubbleBorderColor.trim()) || DEFAULT_CONFIG.bubbleBorderColor;
    config.bubbleBounce = `${config.bubbleBounce}` === 'true';
    // leadFormEnabled: undefined veya 'true' ise true, sadece 'false' ise false
    config.leadFormEnabled = config.leadFormEnabled === undefined || `${config.leadFormEnabled}` !== 'false';
    if (config.inline) {
      config.bubbleLauncher = false;
    }
    config.launcherIcon = config.launcherIcon || DEFAULT_CONFIG.launcherIcon;
    config.launcherImage = (config.launcherImage && `${config.launcherImage}`.trim()) || '';
    config.launcherAriaLabel = config.launcherAriaLabel || DEFAULT_CONFIG.launcherAriaLabel;
    return config;
  }

  getHostElement() {
    if (this.config.mountSelector) {
      const target = document.querySelector(this.config.mountSelector);
      if (target) {
        return target;
      }
      console.warn('[aifinex-panel] mountSelector hedefi bulunamadı, body kullanılacak.');
    }
    return document.body;
  }

  getPanelOffsets() {
    if (!this.useBubbleLauncher) {
      return { x: this.config.offsetX, y: this.config.offsetY };
    }
    const gap = this.config.bubblePanelGap;
    return {
      x: this.config.bubbleOffsetX + this.config.bubbleSize + gap,
      y: this.config.bubbleOffsetY + this.config.bubbleSize + gap
    };
  }

  resolveAssetBase() {
    if (this.config.assetBase) {
      return ensureTrailingSlash(this.config.assetBase);
    }
    if (this.script && this.script.src) {
      const url = new URL(this.script.src, window.location.href);
        url.pathname = url.pathname.replace(/\/[^/]*$/, '/');
      return url.href;
    }
    return ensureTrailingSlash(window.location.origin);
  }

  resolveAssetUrl(path) {
    if (!path) {
      return '';
    }
    if (/^(?:https?:)?\/\//i.test(path) || path.startsWith('data:')) {
      return path;
    }
    try {
      const sanitized = path.replace(/^\/+/, '');
      return new URL(sanitized, this.assetBase).toString();
    } catch (error) {
      console.warn('[aifinex-panel] Unable to resolve asset url for', path, error);
      return path;
    }
  }

  resolveFrameUrl() {
    const framePath = this.config.framePath || DEFAULT_CONFIG.framePath;
    if (/^https?:/.test(framePath)) {
      return framePath;
    }
    return `${this.assetBase}${framePath}`;
  }

  mountWhenReady() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.mount(), { once: true });
      return;
    }
    this.mount();
  }

  mount() {
    if (this.root) {
      return;
    }
    injectStyles(STYLE_ID, this.buildStyles());
    this.useBubbleLauncher = !this.config.inline && this.config.bubbleLauncher;
    const forcedPosition = this.useBubbleLauncher ? this.config.bubblePosition : this.config.position;
    const position = normalizePosition(forcedPosition);
    const { x: offsetX, y: offsetY } = this.getPanelOffsets();
    const host = this.getHostElement();
    const widthPx = `${this.config.panelWidth}px`;
    const heightPx = `${this.config.panelHeight}px`;
    this.root = createElement('div', {
      className: `aifinex-panel aifinex-panel--${position}`
    });
    this.root.setAttribute('data-widget-root', '');
    if (this.config.inline) {
      const isBodyHost = host === document.body;
      this.root.classList.add('aifinex-panel--inline');
      applyStyles(this.root, {
        position: 'relative',
        zIndex: String(this.config.zIndex),
        width: isBodyHost ? widthPx : '100%',
        height: isBodyHost ? heightPx : '100%',
        borderRadius: `${this.config.borderRadius}px`
      });
      if (isBodyHost) {
        Object.assign(this.root.style, {
          maxWidth: `min(100%, ${widthPx})`,
          maxHeight: `min(100vh, ${heightPx})`,
          margin: '40px auto',
          display: 'block'
        });
      }
    } else {
      applyStyles(this.root, {
        position: 'fixed',
        zIndex: String(this.config.zIndex),
        width: widthPx,
        height: heightPx,
        maxWidth: `calc(100vw - ${offsetX * 2}px)`,
        maxHeight: `calc(100vh - ${offsetY * 2}px)`,
        borderRadius: `${this.config.borderRadius}px`,
        top: 'auto',
        bottom: `${offsetY}px`,
        right: position === 'bottom-right' ? `${offsetX}px` : 'auto',
        left: position === 'bottom-left' ? `${offsetX}px` : 'auto'
      });
    }
    if (this.config.themeColor) {
      this.root.style.setProperty('--panel-accent', this.config.themeColor);
    }

    this.iframe = this.createFrame();
    this.root.appendChild(this.iframe);

    host.appendChild(this.root);

    if (this.useBubbleLauncher) {
      this.setupBubbleLauncher();
      this.hide();
    }

    window.addEventListener('message', this.handleMessage);
  }

  createFrame() {
    const iframe = createElement('iframe', {
      className: 'aifinex-panel__iframe',
      attrs: {
        src: this.composeFrameSrc(),
        title: this.config.frameTitle,
        allow: 'clipboard-write; microphone;'
      }
    });
    iframe.setAttribute('data-panel-frame', '');
    iframe.setAttribute('loading', 'lazy');
    return iframe;
  }

  composeFrameSrc() {
    try {
      const url = new URL(this.frameUrl, window.location.href);
      const params = url.searchParams;
      if (this.config.n8nUrl) {
        params.set('n8nUrl', this.config.n8nUrl);
      }
      if (this.config.sessionId) {
        params.set('sessionId', this.config.sessionId);
      }
      if (this.config.brandName) {
        params.set('brandName', this.config.brandName);
      }
      if (this.config.brandLogo) {
        const logoUrl = this.resolveAssetUrl(this.config.brandLogo);
        if (logoUrl) {
          params.set('brandLogo', logoUrl);
        }
      }
      if (this.config.welcomeMessage) {
        params.set('welcomeMessage', this.config.welcomeMessage);
      }
      if (this.config.navColor) {
        params.set('navColor', this.config.navColor);
      }
      if (this.config.bodyColor) {
        params.set('bodyColor', this.config.bodyColor);
      }
      if (this.config.brandNameColor) {
        params.set('brandNameColor', this.config.brandNameColor);
      }
      if (this.config.sendButtonColor) {
        params.set('sendButtonColor', this.config.sendButtonColor);
      }
      if (this.config.sendIconColor) {
        params.set('sendIconColor', this.config.sendIconColor);
      }
      if (this.config.userMessageColor) {
        params.set('userMessageColor', this.config.userMessageColor);
      }
      if (this.config.leadWebhookUrl) {
        params.set('leadWebhookUrl', this.config.leadWebhookUrl);
      }
      params.set('leadFormEnabled', this.config.leadFormEnabled ? 'true' : 'false');
      params.set('hideMenuClose', this.config.showCloseButton ? 'true' : 'false');
      return url.toString();
    } catch (error) {
      console.warn('[aifinex-widget] Unable to decorate frame url', error);
      return this.frameUrl;
    }
  }

  handleMessage(event) {
    if (!event.data || typeof event.data !== 'object') {
      return;
    }
    if (event.data.type === 'AIFINEX_WIDGET_CLOSE') {
      this.hide();
    }
  }

  hide() {
    this.root?.classList.add('aifinex-panel--hidden');
  }

  show() {
    if (!this.root) {
      return;
    }
    this.root.classList.remove('aifinex-panel--hidden');
  }

  handleLauncherClick() {
    if (!this.root) {
      return;
    }
    const isHidden = this.root.classList.contains('aifinex-panel--hidden');
    if (isHidden) {
      this.show();
    } else {
      this.hide();
    }
  }

  handleDocumentPointer(event) {
    if (!this.useBubbleLauncher || !this.root || this.root.classList.contains('aifinex-panel--hidden')) {
      return;
    }
    const target = event.target;
    if (this.root.contains(target) || this.launcher?.contains(target)) {
      return;
    }
    this.hide();
  }

  destroy() {
    window.removeEventListener('message', this.handleMessage);
    if (this.launcher) {
      this.launcher.removeEventListener('click', this.handleLauncherClick);
      this.launcher.removeEventListener('animationend', this.handleLauncherAnimationEnd);
      this.launcher.remove();
      this.launcher = null;
    }
    if (this.documentListenerAttached) {
      document.removeEventListener('pointerdown', this.handleDocumentPointer, true);
      this.documentListenerAttached = false;
    }
    if (this.root) {
      this.root.remove();
      this.root = null;
    }
  }

  setupBubbleLauncher() {
    if (this.launcher || this.config.inline) {
      return;
    }
    this.launcher = this.createLauncher();
    document.body.appendChild(this.launcher);
    this.launcher.addEventListener('click', this.handleLauncherClick);
    if (this.config.bubbleBounce) {
      requestAnimationFrame(() => {
        if (this.launcher) {
          this.launcher.classList.add('aifinex-launcher--bounce');
        }
      });
      this.launcher.addEventListener('animationend', this.handleLauncherAnimationEnd);
    }
    if (!this.documentListenerAttached) {
      document.addEventListener('pointerdown', this.handleDocumentPointer, true);
      this.documentListenerAttached = true;
    }
  }

  handleLauncherAnimationEnd(event) {
    if (event.target !== this.launcher || event.animationName !== 'aifinex-launcher-bounce') {
      return;
    }
    this.launcher.classList.remove('aifinex-launcher--bounce');
    this.launcher.removeEventListener('animationend', this.handleLauncherAnimationEnd);
  }

  createLauncher() {
    const orientation = this.config.bubblePosition === 'bottom-right'
      ? 'aifinex-launcher--right'
      : 'aifinex-launcher--left';
    const button = createElement('button', {
      className: `aifinex-launcher ${orientation}`,
      attrs: {
        type: 'button',
        'aria-label': this.config.launcherAriaLabel || 'Sohbeti aç'
      }
    });
    const size = this.config.bubbleSize;
    button.style.width = `${size}px`;
    button.style.height = `${size}px`;
    const icon = this.config.launcherIcon || '💬';
    if (this.config.launcherImage) {
      const img = createElement('img', {
        className: 'aifinex-launcher__icon',
        attrs: {
          src: this.resolveAssetUrl(this.config.launcherImage),
          alt: icon
        }
      });
      button.appendChild(img);
    } else {
      button.textContent = icon;
    }
    button.style.bottom = `${this.config.bubbleOffsetY}px`;
    if (this.config.bubblePosition === 'bottom-right') {
      button.style.right = `${this.config.bubbleOffsetX}px`;
      button.style.left = 'auto';
    } else {
      button.style.left = `${this.config.bubbleOffsetX}px`;
      button.style.right = 'auto';
    }
    if (this.config.bubbleBackground) {
      button.style.background = this.config.bubbleBackground;
    }
    if (this.config.bubbleBorderColor) {
      button.style.border = `2px solid ${this.config.bubbleBorderColor}`;
    } else {
      button.style.border = 'none';
    }
    return button;
  }

  buildStyles() {
    return `
      .aifinex-panel {
        font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        background: #05060a;
        box-shadow: 0 35px 90px rgba(15, 14, 36, 0.45);
        overflow: hidden;
        display: flex;
        justify-content: stretch;
        align-items: stretch;
        transition: opacity 220ms ease, transform 220ms ease;
      }
      .aifinex-panel--bottom-left {
        right: auto;
      }
      .aifinex-panel--inline {
        box-shadow: none;
        inset: unset !important;
      }
      .aifinex-panel__iframe {
        width: 100%;
        height: 100%;
        border: none;
        display: block;
      }
      .aifinex-panel--hidden {
        opacity: 0;
        pointer-events: none;
        transform: translateY(18px);
      }
      .aifinex-launcher {
        position: fixed;
        bottom: 24px;
        width: 64px;
        height: 64px;
        border-radius: 50%;
        border: none;
        background: linear-gradient(135deg, #5b4bff, #7a6dff);
        color: #fff;
        font-size: 28px;
        line-height: 1;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 18px 30px rgba(0, 0, 0, 0.35);
        cursor: pointer;
        transition: transform 180ms ease, opacity 180ms ease;
        z-index: 2147482001;
      }
      .aifinex-launcher--left {
        left: 24px;
        right: auto;
      }
      .aifinex-launcher--right {
        right: 24px;
        left: auto;
      }
      .aifinex-launcher__icon {
        width: 60%;
        height: 60%;
        object-fit: contain;
        pointer-events: none;
      }
      .aifinex-launcher:hover {
        transform: translateY(-2px) scale(1.03);
      }
      .aifinex-launcher--bounce {
        animation: aifinex-launcher-bounce 0.9s ease-out 2;
      }
      .aifinex-launcher--hidden {
        opacity: 0;
        pointer-events: none;
        transform: scale(0.9);
      }
      @media (max-width: 768px) {
        .aifinex-panel:not(.aifinex-panel--inline) {
          inset: 0 !important;
          width: 100vw !important;
          height: 100vh !important;
          border-radius: 0 !important;
        }
        .aifinex-launcher {
          width: 56px;
          height: 56px;
          bottom: 18px;
          left: 18px;
          box-shadow: 0 14px 24px rgba(0, 0, 0, 0.35);
        }
      }
      @keyframes aifinex-launcher-bounce {
        0% {
          transform: translateY(0) scale(1);
        }
        30% {
          transform: translateY(-16px) scale(1.05);
        }
        55% {
          transform: translateY(0) scale(1);
        }
        75% {
          transform: translateY(-10px) scale(1.03);
        }
        100% {
          transform: translateY(0) scale(1);
        }
      }
    `;
  }
}

const manager = {
  instances: [],
  instance: null,
  init(config) {
    const instance = new AifinexPanel(config);
    this.instances.push(instance);
    this.instance = instance;
    return instance;
  },
  destroy(instance) {
    if (instance) {
      instance.destroy();
      this.instances = this.instances.filter((item) => item !== instance);
      if (this.instance === instance) {
        this.instance = this.instances[this.instances.length - 1] || null;
      }
      return;
    }
    this.instances.forEach((item) => item.destroy());
    this.instances = [];
    this.instance = null;
  }
};

if (!window.AIFINEX_WIDGET) {
  window.AIFINEX_WIDGET = manager;
}

const autoInit = window.AIFINEX_WIDGET_AUTO_INIT !== false;
if (autoInit) {
  window.AIFINEX_WIDGET.init?.();
}

export default manager;
